import { useCallback, useMemo, useState } from 'react';
import { SpaceKind, SpaceNodeSortBy } from 'shared';

import { VaultAccess } from '../../../guards/page-access.util';
import { Order } from '../../../types/general.types';
import { DataRoomModalType } from '../enums/data-room-modal-type.enum';
import { DataRoomModalStore } from '../store/data-room-modal.store';
import { SidebarTreeStore } from '../store/sidebar-tree.store';
import {
  BrowserNodeActions,
  SpaceBrowserNode,
  SpaceBrowserSort,
  SpaceSelection,
} from '../types/data-room.types';
import { foundFolderBreadcrumbs, spaceBrowserPermissions } from '../utils/space-browser.util';
import { useVaultArchive } from '../vault/hooks/use-vault-archive.hook';
import { useVaultFileActions } from '../vault/hooks/use-vault-file-actions.hook';
import { useBrowserSearch } from './use-browser-search.hook';
import { useDownloadSpaceArchive } from './use-download-space-archive.hook';
import { useDownloadSpaceFile } from './use-download-space-file.hook';
import { useVaultAccess } from './use-recipient-view.hook';
import { useSpaceBrowserContents } from './use-space-browser-contents.hook';
import { useViewSpaceFile } from './use-view-space-file.hook';

const ROOT_LOCATION = 'root';

const DEFAULT_SORT: SpaceBrowserSort = { by: SpaceNodeSortBy.NAME, order: Order.ASC };

// The Vault's own search orders by name alone; everywhere else the server orders by every column.
const NAME_SORT_ONLY = [SpaceNodeSortBy.NAME];
const ALL_SORTABLE_BY = [SpaceNodeSortBy.NAME, SpaceNodeSortBy.LAST_EDIT, SpaceNodeSortBy.SIZE];

export const useWorkspaceBrowser = ({
  isRecipientView,
  selection,
}: {
  isRecipientView: boolean;
  selection: SpaceSelection;
}) => {
  const { breadcrumbs, folderId, space } = selection;
  const reveal = SidebarTreeStore((state) => state.reveal);
  const resetSelection = SidebarTreeStore((state) => state.resetSelection);
  const openModal = DataRoomModalStore(({ openModal }) => openModal);
  const isVault = space.kind === SpaceKind.VAULT;
  // A Stakeholder reads the folders of the Vault that are theirs and changes nothing in it.
  const vaultAccess = useVaultAccess();
  const isVaultReadOnly = isVault && vaultAccess === VaultAccess.OWN_FOLDERS;

  // Kept here, not in a view, so it outlives a table's remount for another folder.
  const [sort, setSort] = useState<null | SpaceBrowserSort>(null);
  const { clearSearch, onSearch, search, searchKey } = useBrowserSearch(
    `${space.id}:${folderId ?? ROOT_LOCATION}`,
  );
  // The search hooks trim the term, so a blank box searches nothing and keeps every column.
  const term = search.trim();
  const sortableBy = isVault && term ? NAME_SORT_ONLY : ALL_SORTABLE_BY;
  // A column the open view cannot order by falls back to the default rather than lingering.
  const activeSort = sort && sortableBy.includes(sort.by) ? sort : null;

  const contents = useSpaceBrowserContents({
    folderId,
    isRecipientView,
    isVaultReadOnly,
    search: term,
    sort: activeSort ?? DEFAULT_SORT,
    space,
  });
  const { foundFolders, folders, isSearching, treeSpace, vaultFileLinks } = contents;
  const permissions = spaceBrowserPermissions({ breadcrumbs, isRecipientView, space });

  const vaultActions = useVaultFileActions({
    breadcrumbs,
    enabled: isVault,
    fileLinks: vaultFileLinks,
    folderId,
    isSearching,
    readOnly: isVaultReadOnly,
    spaceId: space.id,
  });
  const download = useDownloadSpaceFile();
  const view = useViewSpaceFile();
  // One per page: it is also what says an archive is being packed.
  const { downloadArchive, pendingArchiveIds } = useDownloadSpaceArchive();
  const { downloadVaultArchive, isPending: isPackingVault } = useVaultArchive();

  const deleteNode = useCallback(
    (node: SpaceBrowserNode) => {
      if (!treeSpace) return;

      openModal({
        metadata: { node: { ...node, spaceKind: treeSpace.kind }, spaceId: treeSpace.id },
        type: DataRoomModalType.DELETE_SPACE_NODE,
      });
    },
    [openModal, treeSpace],
  );

  // One rename for a folder and a file, in a workspace and in a data room alike.
  const renameNode = useCallback(
    (node: SpaceBrowserNode) => {
      if (!treeSpace) return;

      openModal({
        metadata: { node: { ...node, spaceKind: treeSpace.kind }, spaceId: treeSpace.id },
        type: DataRoomModalType.RENAME_SPACE_NODE,
      });
    },
    [openModal, treeSpace],
  );

  const rowActions = useMemo<BrowserNodeActions>(() => {
    if (!treeSpace) return vaultActions;

    return {
      onDeleteFile: permissions.canWrite ? deleteNode : undefined,
      onDeleteFolder: permissions.canWrite ? deleteNode : undefined,
      onDownloadFile: permissions.canDownload
        ? (fileId) => void download({ fileId, space: treeSpace })
        : undefined,
      onDownloadFolder: permissions.canDownload
        ? (id) => void downloadArchive({ folderId: id, space: treeSpace })
        : undefined,
      onOpenFile: (fileId, fileName) => void view({ fileId, fileName, space: treeSpace }),
      onRenameFile: permissions.canWrite ? renameNode : undefined,
      onRenameFolder: permissions.canWrite ? renameNode : undefined,
    };
  }, [
    deleteNode,
    download,
    downloadArchive,
    permissions.canDownload,
    permissions.canWrite,
    renameNode,
    treeSpace,
    vaultActions,
    view,
  ]);

  // For the Vault: the open folder, or the whole Repository at its top level.
  // The whole Repository is every holder's documents together, so the server refuses it to a
  // Stakeholder; a folder of their own is theirs to save.
  const canArchiveVault = isVault && !(isVaultReadOnly && !folderId);
  const archive = {
    download: treeSpace
      ? permissions.canDownload
        ? () => void downloadArchive({ space: treeSpace })
        : undefined
      : canArchiveVault
        ? () => downloadVaultArchive(folderId)
        : undefined,
    // Any archive in flight, a folder's included: one download button shows them all.
    isPacking: isVault ? isPackingVault : pendingArchiveIds.size > 0,
  };

  // A breadcrumb goes back up the known path; a folder in the table goes one level down, and a
  // folder a search found goes straight to where it sits.
  const openNode = useCallback(
    (id: string) => {
      if (id === space.id) {
        reveal({ breadcrumbs: [], folderId: null, space });

        return;
      }

      if (isSearching) {
        const found = foundFolders.find((match) => match.id === id);

        if (!found) return;

        reveal({ breadcrumbs: foundFolderBreadcrumbs(found), folderId: found.id, space });
        // The match may be the open folder itself, so the move alone would not empty the box.
        clearSearch();

        return;
      }

      const crumbIndex = breadcrumbs.findIndex((crumb) => crumb.id === id);

      if (crumbIndex !== -1) {
        reveal({ breadcrumbs: breadcrumbs.slice(0, crumbIndex + 1), folderId: id, space });

        return;
      }

      const folder = folders.find((child) => child.id === id);

      if (!folder) return;

      reveal({
        breadcrumbs: [...breadcrumbs, { id: folder.id, name: folder.name }],
        folderId: folder.id,
        space,
      });
    },
    [breadcrumbs, clearSearch, folders, foundFolders, isSearching, reveal, space],
  );

  // From the space's top level, back to Home.
  const goBack = () => {
    if (!breadcrumbs.length) {
      resetSelection();

      return;
    }

    const parents = breadcrumbs.slice(0, -1);

    reveal({ breadcrumbs: parents, folderId: parents[parents.length - 1]?.id ?? null, space });
  };

  return {
    archive,
    contents,
    navigation: { goBack, openNode },
    permissions,
    rowActions,
    search: { onSearch, searchKey },
    sort: { active: activeSort, setSort, sortableBy },
  };
};
